import React from 'react';
import dayjs from 'dayjs';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import { useCreateDeliveryOrderMutation } from '../queries/createDeliveryOrder.mutation.generated';
import { Locations } from '@gql/base-types';
import ManagedQuoteCard, { isQuoteExpired } from './ManagedQuoteCard';
import { getThirdPartyClient } from '../thirdParty/client';
import { normAddr } from '../utils/stopBuilder';
import type { TpPoint, TpQuote } from '../thirdParty/types';

interface InstantQuoteFormProps {
  customerId: number;
  userEmail: string;
  pickup: TpPoint | null;
  pickupProblem?: string | null;
  booking: boolean;
  savedLocations: (Locations | null)[];
  refetchLocations: () => void;
  /** Book via the shared third-party flow (synthetic single-stop draft run). */
  onBook: (
    syntheticDraftRun: any,
    quote: TpQuote,
    opts?: { departureTime?: string; requireSignature?: boolean }
  ) => Promise<void>;
}

type ReadyKey = 'now' | '30' | '60' | 'schedule';

const READY_OPTIONS: { key: ReadyKey; label: string }[] = [
  { key: 'now', label: 'Now' },
  { key: '30', label: '+30 min' },
  { key: '60', label: '+1 hr' },
  { key: 'schedule', label: 'Schedule later' },
];

/**
 * "⚡ Get an instant quote — no order needed": price a one-off courier delivery
 * to an ad-hoc destination. Booking creates a REAL delivery_orders row first
 * (auto ADHOC order number when no PO is given), then runs the exact
 * order-based booking flow — so the run/queue/returns lifecycle stays honest.
 */
const InstantQuoteForm: React.FC<InstantQuoteFormProps> = ({
  customerId,
  userEmail,
  pickup,
  pickupProblem,
  booking,
  savedLocations,
  refetchLocations,
  onBook,
}) => {
  const [location, setLocation] = React.useState<Locations | null>(null);
  const [business, setBusiness] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [quantity, setQuantity] = React.useState('1');
  const [poNumber, setPoNumber] = React.useState('');
  const [ready, setReady] = React.useState<ReadyKey>('now');
  const [scheduledAt, setScheduledAt] = React.useState('');
  const [requireSignature, setRequireSignature] = React.useState(false);

  const [rawPhase, setPhase] = React.useState<'form' | 'quoting' | 'quoted'>('form');
  const [quote, setQuote] = React.useState<TpQuote | null>(null);
  const [createOrder] = useCreateDeliveryOrderMutation();

  // Hold a reload while any field differs from the blank form. A booking needs
  // a destination, so the form is still dirty while it books. `booking` isn't
  // counted: it's the run builder's shared courier flag, and the run builder's
  // own guard already holds while it's set.
  const holdsUnsavedWork =
    !!location ||
    business !== '' ||
    phone !== '' ||
    description !== '' ||
    poNumber !== '' ||
    quantity !== '1' ||
    ready !== 'now' ||
    requireSignature;
  useReloadGuard(holdsUnsavedWork, 'Instant quote');

  const address = location?.address || '';
  const readyAt = (): string | undefined => {
    switch (ready) {
      case '30': return dayjs().add(30, 'minute').toISOString();
      case '60': return dayjs().add(1, 'hour').toISOString();
      case 'schedule': return scheduledAt ? dayjs(scheduledAt).toISOString() : undefined;
      default: return undefined;
    }
  };

  const canQuote = !!address && !!business.trim() && !pickupProblem && !!pickup && (ready !== 'schedule' || !!scheduledAt);

  // Any form change invalidates existing quotes — the quoted phase only holds
  // for the signature it was quoted against (derived state, no reset effect).
  const formSignature = `${address}|${business}|${quantity}|${ready}|${scheduledAt}`;
  const [quotedSignature, setQuotedSignature] = React.useState<string | null>(null);
  const phase = rawPhase === 'quoted' && quotedSignature !== formSignature ? 'form' : rawPhase;

  const handleQuote = async () => {
    if (!pickup) return;
    setPhase('quoting');
    try {
      const result = await getThirdPartyClient().quote({
        pickup,
        dropoff: {
          name: business.trim(),
          business_name: business.trim(),
          phone: phone.trim() || undefined,
          address,
          lat: (location as any)?.latitude ?? undefined,
          lng: (location as any)?.longitude ?? undefined,
        },
        items: [{ name: description.trim() || 'Parts delivery', quantity: Number(quantity) || 1 }],
        ready_at: readyAt(),
      });
      // Algo placeholder: cheapest wins; the dealer sees one price.
      const best = [...result.quotes].sort((a, b) => a.fee_cents - b.fee_cents)[0] ?? null;
      setQuote(best);
      setQuotedSignature(formSignature);
      setPhase('quoted');
      if (!best) toast.warning('No courier is available for this delivery right now.');
    } catch (err: any) {
      setPhase('form');
      toast.error(`Failed to get quotes: ${err?.message || 'Unknown error'}`);
    }
  };

  const handleBook = async () => {
    if (!quote || isQuoteExpired(quote) || !location) return;
    try {
      // 1. A real pending order — "no PO yet" gets an ADHOC number.
      const orderNumber = poNumber.trim() || `ADHOC-${dayjs().format('YYMMDD-HHmmss')}`;
      const orderRes = await createOrder({
        variables: {
          order: {
            customer_id: customerId,
            order_number: orderNumber,
            recipient_business: business.trim(),
            recipient_name: business.trim(),
            recipient_phone: phone.trim(),
            delivery_address: address,
            delivery_lat: (location as any)?.latitude ?? null,
            delivery_lng: (location as any)?.longitude ?? null,
            item_description: description.trim() || null,
            quantity: Number(quantity) || 1,
            status: 'pending',
            created_by: userEmail,
            updated_by: userEmail,
          },
        },
      });
      const created = orderRes.data?.insert_delivery_orders_one;
      if (!created?.id) throw new Error('Failed to create the order');
      refetchLocations();

      // 2. Same booking flow as order-based 3rd-party runs.
      const dest = {
        key: normAddr(address),
        id: `dstop-${normAddr(address)}`,
        location_id: location?.id ?? null,
        address,
        recipient_business: business.trim(),
        recipient_name: business.trim(),
        recipient_phone: phone.trim(),
        delivery_lat: (location as any)?.latitude ?? null,
        delivery_lng: (location as any)?.longitude ?? null,
        delivery_orders: [
          {
            id: created.id,
            order_number: orderNumber,
            item_description: description.trim() || null,
            quantity: Number(quantity) || 1,
          },
        ],
      };
      await onBook({ stops: [dest] }, quote, { departureTime: readyAt(), requireSignature });
    } catch (err: any) {
      toast.error(`Failed to book: ${err?.message || 'Unknown error'}`);
    }
  };

  return (
    <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid='pd-instant-quote'>
      <Box>
        <Typography variant='body2' sx={{ fontWeight: 700 }}>
          ⚡ Get an instant quote — no order needed
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          One-off delivery from <b>{pickup?.business_name || pickup?.name || 'your rooftop'}</b> (your location).
        </Typography>
      </Box>

      {pickupProblem ? <Alert severity='warning'>{pickupProblem}</Alert> : null}

      <MovePlannerLocationSelect
        rooftopId={customerId}
        required
        label='Destination'
        placeholder='Start typing an address…'
        location={location}
        locations={savedLocations}
        nameOverride={business.trim() || undefined}
        displayValue='address'
        onLocationChange={(loc?: Locations | null) => {
          setLocation(loc ?? null);
          if (loc?.name && !business) setBusiness(loc.name);
        }}
        onLocationFavorite={refetchLocations}
      />

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, sm: 7 }}>
          <TextField
            fullWidth size='small' label='Recipient / shop name' required
            placeholder='e.g. Body Shop Done Right'
            value={business} onChange={e => setBusiness(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField fullWidth size='small' label='Recipient phone' value={phone} onChange={e => setPhone(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 7 }}>
          <TextField fullWidth size='small' label='What’s being delivered' placeholder='e.g. Brake pad set' value={description} onChange={e => setDescription(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 2.5 }}>
          <TextField fullWidth size='small' label='Qty' type='number' value={quantity} onChange={e => setQuantity(e.target.value)} slotProps={{ htmlInput: { min: 1 } }} />
        </Grid>
        <Grid size={{ xs: 6, sm: 2.5 }}>
          <TextField fullWidth size='small' label='PO # (optional)' value={poNumber} onChange={e => setPoNumber(e.target.value)} />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        {READY_OPTIONS.map(o => (
          <Chip
            key={o.key}
            label={o.label}
            size='small'
            onClick={() => setReady(o.key)}
            color={ready === o.key ? 'primary' : 'default'}
            variant={ready === o.key ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
        ))}
        {ready === 'schedule' ? (
          <TextField
            size='small' type='datetime-local' value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            slotProps={{ htmlInput: { min: dayjs().format('YYYY-MM-DDTHH:mm') } }}
          />
        ) : null}
      </Box>

      {phase === 'quoting' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center', py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='body2' color='text.secondary'>
            Pricing this delivery with Uber &amp; DoorDash…
          </Typography>
        </Box>
      ) : null}

      {phase === 'quoted' && quote ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='caption' color='text.secondary'>
            ◔ Estimate — no order created yet · Courier photographs every dropoff
          </Typography>
          <ManagedQuoteCard quote={quote} />
          <FormControlLabel
            control={
              <Switch
                size='small'
                checked={requireSignature}
                onChange={e => setRequireSignature(e.target.checked)}
                disabled={booking}
                data-testid='pd-instant-quote-require-signature'
              />
            }
            label={<Typography variant='body2'>Require recipient signature</Typography>}
            sx={{ ml: 0 }}
          />
        </Box>
      ) : null}

      {phase !== 'quoted' || !quote ? (
        <Button
          fullWidth variant='outlined'
          onClick={handleQuote}
          disabled={!canQuote || phase === 'quoting'}
          data-testid='pd-instant-quote-get'
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {phase === 'quoting' ? 'Getting quote…' : '⚡ Get instant quote'}
        </Button>
      ) : (
        <Button
          fullWidth variant='contained'
          onClick={handleBook}
          disabled={isQuoteExpired(quote) || booking}
          startIcon={booking ? <CircularProgress size={16} color='inherit' /> : undefined}
          data-testid='pd-instant-quote-book'
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {booking
            ? 'Booking…'
            : isQuoteExpired(quote)
              ? 'Quote expired — re-quote'
              : `Create order & book · $${(quote.fee_cents / 100).toFixed(2)}`}
        </Button>
      )}
    </Box>
  );
};

export default InstantQuoteForm;
